import CardCommon, { CardCommonType } from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import React, { useMemo } from 'react';
import AnalysisCard from '../AnalysisCard';
import { percentageFormat, reviewsFormat } from '@op/utils/value';
import ChannelLabel from '@op/components/ChannelLabel';
import { getRationPercentValue } from '@op/utils';
import { formatDateFunc } from '@op/utils/time';
import './index.scss';
import moment from 'moment';

type ISteamRatingCard = {
  data: any;
  loading: boolean;
  error: boolean;
  filter: any;
  wholeGameInfo: any;
  loadingHeight?: number;
} & CardCommonType;
export default function SteamRatingCard({
  data,
  error,
  filter,
  wholeGameInfo,
  ...cardProps
}: ISteamRatingCard) {
  const { formatMessage } = useLanguage();

  const {
    allReviewsPositive,
    recentReviewsPositive,
    allReviewRatio,
    recentReviewRatio,
    allReviewsPositivePercent,
    recentReviewsPositivePercent,
    oldAllReviewsPositive,
    oldRecentReviewsPositive,
  } = useMemo(() => {
    const steamData = data?.steam;
    const oldSteamData = data?.steam_old;

    const allReviewsPositive =
      steamData?.find((item) => item.key === 'all_reviews_recommend')?.review ||
      0;
    const recentReviewsPositive =
      steamData?.find((item) => item.key === 'recent_reviews_recommend')
        ?.review || 0;
    const oldAllReviewsPositive =
      oldSteamData?.find((item) => item.key === 'all_reviews_recommend')
        ?.review || 0;
    const oldRecentReviewsPositive =
      oldSteamData?.find((item) => item.key === 'recent_reviews_recommend')
        ?.review || 0;
    const allReviews =
      steamData?.find((item) => item.key === 'all_reviews')?.review || 0;
    const recentReviews =
      steamData?.find((item) => item.key === 'recent_reviews')?.review || 0;

    const allReviewRatio = getRationPercentValue(
      allReviewsPositive,
      oldAllReviewsPositive,
      6,
    );
    const recentReviewRatio = getRationPercentValue(
      recentReviewsPositive,
      oldRecentReviewsPositive,
      6,
    );
    const allReviewsPositivePercent = (allReviewsPositive / allReviews) * 100;
    const recentReviewsPositivePercent =
      (recentReviewsPositive / recentReviews) * 100;

    return {
      allReviewsPositive,
      recentReviewsPositive,
      allReviewRatio,
      recentReviewRatio,
      allReviewsPositivePercent,
      recentReviewsPositivePercent,
      oldAllReviewsPositive,
      oldRecentReviewsPositive,
    };
  }, [data]);

  const { releasedGame, releaseAfterThirtyDays } = useMemo(() => {
    const releaseDate = moment(wholeGameInfo?.release_date, 'YYYY-MM-DD');
    const startDate = moment(filter?.start_date, 'YYYY-MM-DD');

    return {
      releasedGame: releaseDate.isBefore(startDate),
      releaseAfterThirtyDays: releaseDate.diff(startDate, 'days') > 30,
    };
  }, [wholeGameInfo, filter]);

  return (
    <CardCommon {...cardProps} className="dbo-steam-rating-card">
      <span className="dbo-steam-rating-card-date">
        {`${formatMessage({ id: 'Up to' })} ${formatDateFunc(
          filter?.date_type || 'daily',
        )(data?.end_time)}`}
      </span>

      {/* All Reviews Positive% */}
      <AnalysisCard
        value={allReviewsPositivePercent || 0}
        title={
          <ChannelLabel
            channel_name={'steam'}
            showText
            replaceText={formatMessage({ id: 'All Reviews Positive%' })}
          ></ChannelLabel>
        }
        percentValue={allReviewRatio || 0}
        bubbleProps={{
          showContent: !!allReviewsPositive,
          oldTime: [data?.link_of_start_time, data?.link_of_end_time],
          newTime: [data?.start_time, data?.end_time],
          oldValue: oldAllReviewsPositive,
          newValue: allReviewsPositive,
          formateDate: formatDateFunc(filter?.date_type || 'daily'),
          formateValue: reviewsFormat,
        }}
        formatPercentValue={(value) => percentageFormat(value, '-', 2)}
        formatValue={(value) => percentageFormat(value, '-', 0)}
        contentCard
        empty={releaseAfterThirtyDays}
        hasEmptyImg={!releaseAfterThirtyDays}
        hasEmptyTitle={!releaseAfterThirtyDays}
        hasEmptyDesc={!releaseAfterThirtyDays}
        emptyDescText={'Data available on the 31st day after launch'}
      />

      {/* Recent Reviews Positive% */}
      <AnalysisCard
        value={recentReviewsPositivePercent || 0}
        title={
          <ChannelLabel
            channel_name={'steam'}
            showText
            replaceText={formatMessage({ id: 'Recent Reviews Positive%' })}
          ></ChannelLabel>
        }
        percentValue={recentReviewRatio || 0}
        bubbleProps={{
          showContent: !!recentReviewsPositive,
          oldTime: [data?.link_of_start_time, data?.link_of_end_time],
          newTime: [data?.start_time, data?.end_time],
          oldValue: oldRecentReviewsPositive,
          newValue: recentReviewsPositive,
          formateDate: formatDateFunc(filter?.date_type || 'daily'),
          formateValue: reviewsFormat,
        }}
        formatPercentValue={(value) => percentageFormat(value, '-', 2)}
        formatValue={(value) => percentageFormat(value, '-', 0)}
        contentCard
        empty={releasedGame}
        hasEmptyImg={!releasedGame}
        hasEmptyTitle={!releasedGame}
        hasEmptyDesc={!releasedGame}
        emptyDescText={'Data available after launch'}
      />
    </CardCommon>
  );
}
